
import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSearch } from '../context/SearchContext';

const SearchBar = forwardRef(({ className }, ref) => {
  const { searchTerm, setSearchTerm } = useSearch();
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const wrapperRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const rotatingTexts = [
  'Search for toner...',
  'Search for mist...',
  'Search for sunscreen...',
  'Search for face wash...',
  'Search for serum...',
  'Search for moisturizer...'
];

const [animatedPlaceholder, setAnimatedPlaceholder] = useState('');
const [wordIndex, setWordIndex] = useState(0);
const [charIndex, setCharIndex] = useState(0);
const [isDeleting, setIsDeleting] = useState(false);

useEffect(() => {
  const currentWord = rotatingTexts[wordIndex];
  let typingSpeed = isDeleting ? 100 : 120;

  const type = () => {
    if (!isDeleting) {
      // Typing
      const updated = currentWord.substring(0, charIndex + 1);
      setAnimatedPlaceholder(updated);
      setCharIndex((prev) => prev + 1);

      if (updated === currentWord) {
        // Pause before deleting
        setTimeout(() => setIsDeleting(true), 1000);
      }
    } else {
      // Deleting
      const updated = currentWord.substring(0, charIndex - 1);
      setAnimatedPlaceholder(updated);
      setCharIndex((prev) => prev - 1);

      if (updated === '') {
        // Move to next word
        setIsDeleting(false);
        setWordIndex((prev) => (prev + 1) % rotatingTexts.length);
      }
    }
  };

  const timer = setTimeout(type, typingSpeed);
  return () => clearTimeout(timer);
}, [charIndex, isDeleting, wordIndex]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      const snapshot = await getDocs(collection(db, 'products'));
      const products = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const recommended = products
        .filter(p => p.tags?.includes('recommended') || p.rating >= 4)
        .slice(0, 10);

      setRecommendedProducts(recommended);
    };

    fetchProducts();
  }, []);

  const handleInputChange = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term && location.pathname !== '/products') {
      navigate('/products');
    }
  };

  const goToProduct = (id) => {
    navigate(`/product/${id}`);
    setIsFocused(false);
    setSearchTerm('');
  };

  return (
    <div ref={wrapperRef} className={`relative w-full ${className}`}>
      <div className="relative w-full">
        {/* Search Icon */}
      
        {/* Search Icon */}
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <svg
            className="w-5 h-5 text-400"
            color='#581C87'
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        {/* Cross Button */}
        {isFocused && (
          <button
            onClick={() => {
              setSearchTerm('');
              setIsFocused(false);
            }}
            className="absolute inset-y-0 right-3 flex items-center text-3xl text-gray-500 hover:text-gray-800"
          >
            &times;
          </button>
        )}

        {/* Input Field */}
        <input
          ref={ref}
          type="text"
          value={searchTerm}
          onFocus={() => setIsFocused(true)}
          onChange={handleInputChange}
          placeholder={`${animatedPlaceholder}${animatedPlaceholder ? '|' : ''}`}
          className="w-full px-4 py-2 pl-10 pr-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      {/* Recommended Products Carousel */}
      {isFocused && !searchTerm && (
        // <div className="absolute top-full mt-2 right-0 w-[500px] bg-white shadow-lg border rounded-lg z-50 p-4">
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[80%] lg:left-auto lg:right-0 lg:translate-x-0  lg:w-[500px] bg-white shadow-lg border rounded-lg z-50 p-4">
          <h3 className="text-lg font-bold mb-3">Recommended For You</h3>

          <div className="overflow-x-auto no-scrollbar">
            <div className="flex gap-3 min-w-max">
              {recommendedProducts.map(product => (
                <div
                  key={product.id}
                  onClick={() => goToProduct(product.id)}
                  className="w-1/2 min-w-[160px] max-w-[180px] flex-shrink-0 rounded-lg shadow-sm border hover:shadow-md transition-all duration-300 overflow-hidden bg-white cursor-pointer"
                >
                  <div className="relative">
                    <img
                      src={product.mainImage}
                      alt={product.name}
                      className="w-full h-32 object-cover object-center"
                    />
                    {product.label && (
                      <span className="absolute top-2 left-2 bg-purple-500 text-white text-xs px-2 py-1 rounded">
                        {product.label}
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm font-medium line-clamp-2">{product.name}</h4>
                    {product.price && (
                      <p className="text-purple-600 font-medium mt-1">₹{product.price}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default SearchBar;
